import { Injectable, Injector, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { AccessToken, IDToken } from '@okta/okta-auth-js';
import { environment } from '../../../environments/environment';
import { ShipmentFlowStateService } from '../services/shipment-flow-state.service';
import { isAccessTokenUsable } from './access-token-usability';
import { isOktaAuthRequired, usesDevAuthToken } from './auth-config';
import { OKTA_AUTH } from './okta-auth.token';
import { buildOktaAuthorizeExtraParams } from './build-okta-authorize-extra-params';
import { resolveOktaUri } from './resolve-okta-uri';

export type AuthSessionStatus = 'unknown' | 'authenticated' | 'unauthenticated';

/**
 * Manages Okta PKCE login, callback handling, token access, and session teardown for ESO Lite.
 * Unauthenticated users are redirected to Okta — no in-app auth screens.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  private readonly oktaAuth = inject(OKTA_AUTH, { optional: true });
  private readonly injector = inject(Injector);

  private readonly sessionStatus = signal<AuthSessionStatus>('unknown');
  private loginRedirectInFlight = false;

  /** Current session status for internal auth checks. */
  readonly status = this.sessionStatus.asReadonly();

  /**
   * Initializes auth state on application startup without initiating a login redirect.
   */
  async bootstrap(): Promise<void> {
    if (!isOktaAuthRequired()) {
      this.sessionStatus.set('authenticated');
      return;
    }

    if (!this.oktaAuth) {
      this.sessionStatus.set('unauthenticated');
      return;
    }

    if (this.oktaAuth.isLoginRedirect()) {
      return;
    }

    const authenticated = await this.oktaAuth.isAuthenticated();
    this.sessionStatus.set(authenticated ? 'authenticated' : 'unauthenticated');
  }

  /**
   * Ensures the user is authenticated before protected routes render; redirects to Okta when not.
   */
  async ensureAuthenticatedForNavigation(): Promise<boolean> {
    if (!isOktaAuthRequired()) {
      this.sessionStatus.set('authenticated');
      return true;
    }

    if (!this.oktaAuth) {
      await this.login();
      return false;
    }

    const authenticated = await this.oktaAuth.isAuthenticated();
    if (authenticated) {
      this.sessionStatus.set('authenticated');
      return true;
    }

    this.sessionStatus.set('unauthenticated');
    await this.login();
    return false;
  }

  /**
   * Completes the Okta redirect callback, then routes to Get Labels or Ship From based on
   * whether leftover wizard state belongs to the signed-in user.
   */
  async handleLoginCallback(): Promise<void> {
    if (!isOktaAuthRequired() || !this.oktaAuth) {
      await this.router.navigateByUrl('/');
      return;
    }

    if (this.readOAuthErrorFromUrl()) {
      await this.clearSession();
      await this.login();
      return;
    }

    try {
      await this.oktaAuth.handleLoginRedirect();
      this.sessionStatus.set('authenticated');

      const originalUri = this.oktaAuth.getOriginalUri() ?? '/';
      this.oktaAuth.removeOriginalUri();
      const reconciled = this.injector
        .get(ShipmentFlowStateService)
        .reconcileAfterLogin(this.getIdentitySubject());
      await this.router.navigateByUrl(reconciled ?? originalUri);
    } catch {
      await this.clearSession();
      await this.login();
    }
  }

  /**
   * Starts the Okta hosted login redirect (PKCE).
   */
  async login(): Promise<void> {
    if (!isOktaAuthRequired() || !this.oktaAuth || this.loginRedirectInFlight) {
      return;
    }

    this.loginRedirectInFlight = true;
    try {
      const extraParams = buildOktaAuthorizeExtraParams();
      await this.oktaAuth.signInWithRedirect(extraParams ? { extraParams } : {});
    } catch {
      this.loginRedirectInFlight = false;
    }
  }

  /**
   * Ends the ESO Lite session through Okta federated sign-out, then redirects to purolator.com.
   * Keeps tokens until revoke + logout redirect so id_token_hint can end the Okta session.
   * On Okta failure/timeout, clears tokens locally and shows the public /signed-out page
   * (avoids the auth-guard loop that would send the user back to Okta login).
   */
  async logout(): Promise<void> {
    this.sessionStatus.set('unauthenticated');
    this.loginRedirectInFlight = false;

    if (!isOktaAuthRequired()) {
      await this.router.navigateByUrl('/signed-out');
      return;
    }

    if (!this.oktaAuth) {
      await this.clearSession();
      await this.router.navigateByUrl('/signed-out');
      return;
    }

    try {
      await this.signOutWithTimeout();
    } catch {
      await this.clearSession();
      await this.router.navigateByUrl('/signed-out');
    }
  }

  /**
   * Returns a usable Okta access token for protected API calls, renewing silently when expired.
   */
  async getAccessToken(): Promise<string | null> {
    if (usesDevAuthToken()) {
      return null;
    }

    const token = await this.readUsableAccessToken();
    return token?.accessToken ?? null;
  }

  /**
   * Returns the signed-in Okta subject (`sub`) for binding wizard state to a user.
   * Prefers the ID token and falls back to the access token when `sub` is absent.
   */
  getIdentitySubject(): string | null {
    if (!isOktaAuthRequired() || !this.oktaAuth) {
      return null;
    }

    const { idToken, accessToken } = this.oktaAuth.tokenManager.getTokensSync();
    return this.readSubjectClaim(idToken) ?? this.readSubjectClaim(accessToken);
  }

  /**
   * Attempts a silent Okta token renewal without redirecting the user to login.
   */
  async tryRecoverSessionSilently(): Promise<boolean> {
    if (!isOktaAuthRequired() || !this.oktaAuth) {
      return false;
    }

    const token = await this.renewAccessTokenSilently();
    return token?.accessToken != null;
  }

  /**
   * Clears invalid auth state after silent recovery fails and redirects to Okta login.
   */
  async handleUnauthorized(_status: number): Promise<void> {
    if (!isOktaAuthRequired()) {
      return;
    }

    await this.clearSession();
    await this.login();
  }

  /**
   * Removes Okta tokens from the token manager without initiating sign-out redirect.
   */
  async clearSession(): Promise<void> {
    this.sessionStatus.set('unauthenticated');
    this.loginRedirectInFlight = false;

    if (!this.oktaAuth) {
      return;
    }

    this.oktaAuth.tokenManager.clear();
  }

  /**
   * Reads a non-expired access token from Okta, renewing silently when needed.
   */
  private async readUsableAccessToken(): Promise<AccessToken | undefined> {
    if (!this.oktaAuth) {
      return undefined;
    }

    const stored = (await this.oktaAuth.tokenManager.get('accessToken')) as AccessToken | undefined;
    if (isAccessTokenUsable(stored)) {
      return stored;
    }

    return this.renewAccessTokenSilently();
  }

  /**
   * Renews the Okta access token without a full login redirect.
   */
  private async renewAccessTokenSilently(): Promise<AccessToken | undefined> {
    if (!this.oktaAuth) {
      return undefined;
    }

    try {
      return (await this.oktaAuth.tokenManager.renew('accessToken')) as AccessToken | undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Revokes Okta tokens then redirects to the federated logout URL.
   * Abandonable: after timeout the in-flight revoke is ignored so a late
   * `window.location.assign` cannot yank the browser away from /signed-out.
   */
  private async signOutWithTimeout(): Promise<void> {
    const oktaAuth = this.oktaAuth!;
    const postLogoutRedirectUri = resolveOktaUri(environment.okta.postLogoutRedirectUri);
    let abandoned = false;

    const federatedSignOut = (async (): Promise<void> => {
      const { accessToken, refreshToken, idToken } = oktaAuth.tokenManager.getTokensSync();

      if (refreshToken) {
        await oktaAuth.revokeRefreshToken(refreshToken);
      }
      if (abandoned) {
        return;
      }

      if (accessToken) {
        await oktaAuth.revokeAccessToken(accessToken);
      }
      if (abandoned) {
        return;
      }

      // Runtime method on OktaAuth; omitted from the published TypeScript surface.
      const logoutUri = (
        oktaAuth as unknown as {
          getSignOutRedirectUrl: (options: {
            idToken?: IDToken;
            postLogoutRedirectUri?: string | null;
          }) => string;
        }
      ).getSignOutRedirectUrl({
        idToken,
        postLogoutRedirectUri,
      });

      oktaAuth.tokenManager.clear();

      if (abandoned) {
        return;
      }

      if (logoutUri) {
        window.location.assign(logoutUri);
        return;
      }

      await oktaAuth.closeSession();
      if (!abandoned) {
        window.location.assign(postLogoutRedirectUri);
      }
    })();

    // Prevent unhandled rejection after the timeout path has already recovered.
    void federatedSignOut.catch(() => undefined);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        federatedSignOut,
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            abandoned = true;
            reject(new Error('Okta sign-out timed out'));
          }, OKTA_SIGNOUT_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Reads a non-empty `sub` claim from an Okta token, if present.
   */
  private readSubjectClaim(token: { claims?: { sub?: string } } | undefined): string | null {
    const subject = token?.claims?.sub?.trim();
    return subject ? subject : null;
  }

  private readOAuthErrorFromUrl(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return new URLSearchParams(window.location.search).get('error');
  }
}

/** Max time to wait for Okta hosted sign-out before showing the local signed-out confirmation. */
const OKTA_SIGNOUT_TIMEOUT_MS = 8_000;
